// Markdown for commentary and post bodies. One renderer, used by the client
// today and by server-side rendering later, so a draft previews exactly as it
// will publish.
//
// Raw HTML is disabled: the output is safe by construction, with no sanitiser
// to keep in step. Five extensions beyond CommonMark: `[[Neh 1:5]]` becomes a
// link to that chapter, a line holding only `{{Neh 1:5-11}}` quotes that
// passage in a post, a ```chart block draws a chart there (charts.ts), a bare
// `[3]` becomes a marker pointing at the third citation at the foot of the
// chapter, and, given a glossary, every word in it is marked for its definition.
// A link off the site opens in a new tab, so a reader never loses the page.
import MarkdownItCtor from "markdown-it";
import footnote from "markdown-it-footnote";
import { parseChart } from "./charts.js";
import { termAttrs } from "./glossary.js";
import { parseRef, toDisplay, toOsis, toSlug } from "./refs.js";
/**
 * `/commentary/nehemiah/1#v5` for a parsed reference. Chapters live at one
 * address: the scripture and the commentary on it are the same page.
 */
export function refHref(ref) {
    return `/commentary/${toSlug(ref)}${ref.verse !== undefined ? `#v${ref.verse}` : ""}`;
}
/** The id of a chapter's nth citation, shared by the marker and the entry. */
export const citationId = (n) => `cite-${n}`;
/** Inline rule for `[[Neh 1:5]]`. Unparseable references are left as typed. */
function scriptureRefs(md) {
    md.inline.ruler.before("link", "scripture_ref", (state, silent) => {
        const src = state.src;
        if (src.charCodeAt(state.pos) !== 0x5b || src.charCodeAt(state.pos + 1) !== 0x5b)
            return false;
        const close = src.indexOf("]]", state.pos + 2);
        if (close < 0)
            return false;
        const raw = src.slice(state.pos + 2, close).trim();
        if (!raw || raw.length > 60)
            return false;
        let ref;
        try {
            ref = parseRef(raw);
        }
        catch {
            return false;
        }
        if (!silent) {
            const open = state.push("link_open", "a", 1);
            open.attrs = [
                ["href", refHref(ref)],
                ["class", "ref-link"],
            ];
            state.push("text", "", 0).content = toDisplay(ref);
            state.push("link_close", "a", -1);
        }
        state.pos = close + 2;
        return true;
    });
}
/**
 * Inline rule for a citation marker: `[3]` becomes a superscript link to the
 * chapter's third reference. A `[3]` that opens a markdown link or reference
 * (`[3](url)`, `[3][x]`) is left to markdown-it.
 */
function citationRefs(md) {
    md.inline.ruler.before("link", "citation_ref", (state, silent) => {
        const src = state.src;
        if (src.charCodeAt(state.pos) !== 0x5b)
            return false;
        const m = /^\[(\d{1,3})\]/.exec(src.slice(state.pos, state.pos + 6));
        if (!m)
            return false;
        const after = src.charAt(state.pos + m[0].length);
        if (after === "(" || after === "[")
            return false;
        if (!silent) {
            const open = state.push("citation_open", "sup", 1);
            open.attrs = [["class", "cite"]];
            const link = state.push("link_open", "a", 1);
            link.attrs = [["href", `#${citationId(Number(m[1]))}`]];
            state.push("text", "", 0).content = m[1];
            state.push("link_close", "a", -1);
            state.push("citation_close", "sup", -1);
        }
        state.pos += m[0].length;
        return true;
    });
}
/**
 * Block rule for a quotation: a line holding nothing but `{{Neh 1:5-11}}`. A
 * post asks for scripture this way rather than having it pasted in, because
 * pasted ESV text would be stored in the post — outside the bounded cache,
 * which is the only place the licence lets us keep any. The words are fetched
 * when the post is read; `renderWithQuotes` hands the page each reference.
 *
 * Wherever quotations are not fetched — a chapter's notes, an excerpt, the
 * feed — the line is a link to the passage, the same as `[[Neh 1:5]]`.
 */
function scriptureQuotes(md) {
    md.block.ruler.before("paragraph", "scripture_quote", (state, startLine, _endLine, silent) => {
        if (state.sCount[startLine] - state.blkIndent >= 4)
            return false; // an indented code block
        const line = state.src.slice(state.bMarks[startLine] + state.tShift[startLine], state.eMarks[startLine]).trim();
        const m = /^\{\{([^{}]{1,60})\}\}$/.exec(line);
        if (!m)
            return false;
        let ref;
        try {
            ref = parseRef(m[1]);
        }
        catch {
            return false;
        }
        if (silent)
            return true;
        const token = state.push("scripture_quote", "", 0);
        token.meta = { osis: toOsis(ref), display: toDisplay(ref), href: refHref(ref) };
        token.map = [startLine, startLine + 1];
        state.line = startLine + 1;
        return true;
    }, 
    // It may end the paragraph above it, so it needs no blank line before it.
    { alt: ["paragraph"] });
    md.renderer.rules.scripture_quote = (tokens, idx) => {
        const { href, display } = tokens[idx].meta;
        return `<p><a href="${md.utils.escapeHtml(href)}" class="ref-link">${md.utils.escapeHtml(display)}</a></p>\n`;
    };
}
/** The site's own hosts: a link to one of them stays in the tab. */
const SITE_HOST = /(^|\.)thebibleunleashed\.com$/i;
/** True for an http(s) or protocol-relative URL on someone else's host. */
export function isExternalHref(href) {
    const m = /^(?:https?:)?\/\/([^/?#:]+)/i.exec(href.trim());
    return !!m && !SITE_HOST.test(m[1]);
}
/**
 * Every link off the site opens in a new tab: the reader keeps their place
 * here. `noreferrer` also keeps the other site from reaching back through
 * `window.opener`.
 */
function externalLinks(md) {
    const open = md.renderer.rules.link_open ?? ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));
    md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
        const token = tokens[idx];
        if (isExternalHref(String(token.attrGet("href") ?? ""))) {
            token.attrSet("target", "_blank");
            token.attrSet("rel", "noopener noreferrer");
        }
        return open(tokens, idx, options, env, self);
    };
}
const isChart = (t) => t.type === "fence" && t.info.trim().toLowerCase() === "chart";
/**
 * A ```chart block (charts.ts). A post's page draws it — `renderParts` hands
 * over the spec — and in a chapter's notes, where charts are not drawn, it is
 * its title; an excerpt leaves it out (`APPARATUS`). One that cannot be read
 * stays the code block it was typed as; a post holding one is refused on save.
 */
function charts(md) {
    const fence = md.renderer.rules.fence;
    md.renderer.rules.fence = (tokens, idx, options, env, self) => {
        const result = isChart(tokens[idx]) ? parseChart(tokens[idx].content) : null;
        if (!result?.ok)
            return fence(tokens, idx, options, env, self);
        return `<p class="chart-ref">Chart: ${md.utils.escapeHtml(result.chart.title)}</p>\n`;
    };
}
/**
 * Glossary terms, wrapped for the definition popover. A core rule that runs
 * last, over finished text tokens, so it sees the words as they will read —
 * curled apostrophes and all. It never looks inside a link: a term in link
 * text, or in a `[[Neh 1:5]]` reference, belongs to the link. Nor in the
 * footnotes, which markdown-it-footnote has gathered at the end by now: they
 * are a body's citations, left as written like a chapter's (`plain`), or
 * "Michael S. Heiser" would carry the archangel's definition.
 */
function glossaryTerms(md) {
    md.core.ruler.push("glossary_terms", (state) => {
        const terms = state.env.terms;
        if (!terms)
            return;
        const text = (content) => {
            const t = new state.Token("text", "", 0);
            t.content = content;
            return t;
        };
        let inNotes = false;
        for (const block of state.tokens) {
            if (block.type === "footnote_block_open")
                inNotes = true;
            else if (block.type === "footnote_block_close")
                inNotes = false;
            if (inNotes || block.type !== "inline" || !block.children)
                continue;
            const out = [];
            let inLink = 0;
            for (const tok of block.children) {
                if (tok.type === "link_open")
                    inLink++;
                else if (tok.type === "link_close")
                    inLink--;
                const found = tok.type === "text" && !inLink ? terms.find(tok.content) : [];
                if (!found.length) {
                    out.push(tok);
                    continue;
                }
                let at = 0;
                for (const m of found) {
                    if (m.start > at)
                        out.push(text(tok.content.slice(at, m.start)));
                    const open = new state.Token("term_open", "span", 1);
                    open.attrs = termAttrs(m.id);
                    out.push(open, text(tok.content.slice(m.start, m.end)), new state.Token("term_close", "span", -1));
                    at = m.end;
                }
                if (at < tok.content.length)
                    out.push(text(tok.content.slice(at)));
            }
            block.children = out;
        }
    });
}
const md = new MarkdownItCtor({ html: false, linkify: true, typographer: true, breaks: false })
    .use(footnote)
    .use(scriptureRefs)
    .use(scriptureQuotes)
    .use(charts)
    .use(citationRefs)
    .use(externalLinks)
    .use(glossaryTerms);
/** `terms` marks the glossary's words; without it the prose is rendered as written. */
export function renderMarkdown(source, terms) {
    return md.render(source ?? "", { terms });
}
/** Single-paragraph render, for headings and other one-line fields. */
export function renderMarkdownInline(source) {
    return md.renderInline(source ?? "");
}
/**
 * The quotations a page can cut out and fill. Only one standing on its own at
 * the top level: cutting one out of a list or a blockquote would split that
 * list's markup in two, so there it stays a link. A chart is cut on the same
 * terms, and nested, stays its title.
 */
const isQuote = (t) => t.type === "scripture_quote" && t.level === 0;
/**
 * A post rendered with its `{{…}}` quotations left for the page to fetch and
 * its charts for the page to draw. The document is parsed once and its tokens
 * cut at each, so a footnote marked above a quotation and written out below
 * it still links up.
 *
 * `quotes: false` is a chapter's commentary: the page already holds its
 * chapter of the ESV, so a quotation there stays a link and only the charts
 * are cut out to draw.
 */
export function renderParts(source, terms, { quotes = true } = {}) {
    const env = { terms };
    const tokens = md.parse(source ?? "", env);
    const parts = [];
    let from = 0;
    const prose = (to) => {
        const html = md.renderer.render(tokens.slice(from, to), md.options, env);
        if (html.trim())
            parts.push({ kind: "prose", html });
    };
    tokens.forEach((t, i) => {
        let part;
        if (quotes && isQuote(t)) {
            const { osis, display } = t.meta;
            part = { kind: "quote", osis, display };
        }
        else if (isChart(t) && t.level === 0) {
            const result = parseChart(t.content);
            part = result.ok ? { kind: "chart", chart: result.chart } : { kind: "bad-chart", error: result.error };
        }
        else {
            return;
        }
        prose(i);
        parts.push(part);
        from = i + 1;
    });
    prose(tokens.length);
    return parts;
}
/** Every chart block in a body, read, wherever it stands: what a save checks. */
export function chartsIn(source) {
    return md
        .parse(source ?? "", {})
        .filter(isChart)
        .map((t) => parseChart(t.content));
}
/** Every passage a body quotes, in order: what the page will fetch when it is read. */
export function quotedRefs(source) {
    return md
        .parse(source ?? "", {})
        .filter(isQuote)
        .map((t) => parseRef(t.meta.osis));
}
/** Tags that end a line of prose; everything else sits inside one. */
const BLOCK_END = /<\/(?:p|h[1-6]|li|blockquote|div|tr|td|th)>|<br\s*\/?>/gi;
/**
 * Apparatus, not prose: footnote and citation markers, the footnotes
 * markdown-it gathers at the end, and charts. An excerpt is the opening lines
 * as they read, and they do not read "wept.[1]", nor end "Kidner, 78. ↩︎",
 * nor break off for "Chart: Years each king reigned".
 */
const APPARATUS = /<sup class="(?:footnote-ref|cite)">.*?<\/sup>|<p class="chart-ref">.*?<\/p>|<hr class="footnotes-sep"[^>]*>[\s\S]*$/g;
/** First `limit` characters of the prose, for excerpts and meta descriptions. */
export function markdownExcerpt(source, limit = 200) {
    const text = renderMarkdown(source)
        .replace(APPARATUS, "")
        // A block boundary is a space; an inline tag is not. Turning <em> into a
        // space too is what makes "He *wept*, then" come out as "He wept , then".
        .replace(BLOCK_END, " ")
        .replace(/<[^>]+>/g, "")
        .replace(/\s+/g, " ")
        .trim();
    if (text.length <= limit)
        return text;
    return `${text.slice(0, text.lastIndexOf(" ", limit) || limit).trimEnd()}…`;
}
//# sourceMappingURL=markdown.js.map